import type { ComponentType, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface MarketingHeaderProps {
	title?: string;
	titleIcon?: ComponentType<{ className?: string }>;
	heading: string;
	/** Use `h2` for section headers when the page already has a primary `h1`. */
	headingAs?: "h1" | "h2";
	description?: string;
	/** When true, `description` is rendered as HTML (e.g. links in changelog pages). */
	descriptionHtml?: boolean;
	align?: "left" | "center";
	actions?: ReactNode;
	className?: string;
}

const animEyebrow =
	"animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both [animation-delay:80ms]";
const animHeading =
	"animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both [animation-delay:140ms]";
const animDescription =
	"animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both [animation-delay:200ms]";
const animActions =
	"animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both [animation-delay:260ms]";

/** Page hero — Geist display (see `.marketing-display-font` in main.css). */
export function MarketingHeader({
	title,
	titleIcon: TitleIcon,
	heading,
	headingAs = "h1",
	description,
	descriptionHtml = false,
	align = "left",
	actions,
	className,
}: MarketingHeaderProps) {
	const center = align === "center";
	const HeadingTag = headingAs;

	return (
		<header
			className={cn(
				"mb-12 flex flex-col gap-4 sm:mb-16",
				center ? "items-center text-center" : null,
				className,
			)}
		>
			{(title || TitleIcon) && (
				<div
					className={cn(
						"inline-flex items-center gap-2 rounded-full border border-border/80 bg-muted/40 px-3 py-1.5 text-sm font-medium text-primary",
						animEyebrow,
						!center && "self-start",
					)}
				>
					{TitleIcon ? <TitleIcon className="size-4 shrink-0" /> : null}
					{title ? <span>{title}</span> : null}
				</div>
			)}
			<HeadingTag
				className={cn(
					"max-w-[24ch] text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl lg:text-6xl",
					animHeading,
					center && "mx-auto",
				)}
			>
				{heading}
			</HeadingTag>
			{description ? (
				descriptionHtml ? (
					<div
						className={cn(
							"max-w-[48ch] text-lg text-pretty text-muted-foreground",
							animDescription,
							center && "mx-auto",
						)}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Controlled marketing copy; opt-in via descriptionHtml.
						dangerouslySetInnerHTML={{ __html: description }}
					/>
				) : (
					<p
						className={cn(
							"max-w-[48ch] text-lg text-pretty text-muted-foreground",
							animDescription,
							center && "mx-auto",
						)}
					>
						{description}
					</p>
				)
			) : null}
			{actions ? (
				<div
					className={cn(
						"flex flex-wrap gap-3",
						animActions,
						center && "justify-center",
						!center && "pt-2",
					)}
				>
					{actions}
				</div>
			) : null}
		</header>
	);
}
